/* MAQUETA VISUAL (datos de ejemplo) — WSS Game Zone. Pedido 28/10/2026: una
   pestaña propia para los juegos de Wordwall y Kahoot, ordenados por tema y
   dentro de cada tema por nivel. Sube juegos la administración y los
   profesores; la ven los matriculados y los pases. Nada de esto está
   conectado: es para que la dueña lo apruebe antes de armarlo. */
import { useState } from "react";
import {
  Gamepad2, Play, Star, ExternalLink, Plus, X, Sparkles, BookA, Globe2, Ear,
  GraduationCap, Trophy,
} from "lucide-react";

const Etiqueta = ({ children, className = "" }) => (
  <div className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${className}`}>{children}</div>
);

const Bloque = ({ titulo, nota, children, testId }) => (
  <section className="mb-16" data-testid={testId}>
    <div className="mb-6 flex items-baseline gap-3 flex-wrap">
      <h2 className="font-display text-lg font-bold tracking-tight text-slate-900">{titulo}</h2>
      <p className="text-sm text-slate-500">{nota}</p>
    </div>
    {children}
  </section>
);

const FUENTE = {
  wordwall: { label: "Wordwall", chip: "bg-violet-100 text-violet-700", punto: "bg-violet-500" },
  kahoot: { label: "Kahoot", chip: "bg-rose-100 text-rose-700", punto: "bg-rose-500" },
};

const NIVEL_CHIP = "bg-slate-900 text-white";

const TEMAS = [
  {
    key: "verbos", nombre: "Verbos", icon: Sparkles, color: "text-orange-600 bg-orange-50",
    juegos: [
      { t: "Presente irregular: ruleta", f: "wordwall", n: "A1", v: 184 },
      { t: "Pretérito vs. imperfecto", f: "kahoot", n: "A2", v: 97 },
      { t: "Subjuntivo: une las parejas", f: "wordwall", n: "B1", v: 56 },
      { t: "Verbos reflexivos: carrera", f: "wordwall", n: "A2", v: 73 },
    ],
  },
  {
    key: "vocabulario", nombre: "Vocabulario", icon: BookA, color: "text-blue-700 bg-blue-50",
    juegos: [
      { t: "La feria del agricultor", f: "wordwall", n: "A1", v: 142 },
      { t: "En la soda: pedí tu casado", f: "kahoot", n: "A1", v: 118 },
      { t: "El cuerpo humano", f: "wordwall", n: "A2", v: 64 },
    ],
  },
  {
    key: "cultura", nombre: "Cultura tica", icon: Globe2, color: "text-emerald-700 bg-emerald-50",
    juegos: [
      { t: "Dichos y pachucadas", f: "kahoot", n: "B1", v: 203 },
      { t: "Provincias y volcanes", f: "wordwall", n: "A2", v: 88 },
    ],
  },
  {
    key: "pronunciacion", nombre: "Pronunciación", icon: Ear, color: "text-teal-700 bg-teal-50",
    juegos: [
      { t: "La rr que no se rinde", f: "wordwall", n: "A1", v: 51 },
    ],
  },
];

const DESTACADOS = [
  { t: "Dichos y pachucadas", f: "kahoot", n: "B1", tema: "Cultura tica" },
  { t: "Presente irregular: ruleta", f: "wordwall", n: "A1", tema: "Verbos" },
  { t: "La feria del agricultor", f: "wordwall", n: "A1", tema: "Vocabulario" },
];

const GameCard = ({ g, onPlay }) => (
  <button onClick={onPlay}
          className="group text-left bg-white border border-slate-200 rounded-2xl p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-900/5 hover:border-slate-300">
    <div className="flex items-center justify-between gap-2">
      <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full ${FUENTE[g.f].chip}`}>
        <span className={`h-1.5 w-1.5 rounded-full ${FUENTE[g.f].punto}`} /> {FUENTE[g.f].label}
      </span>
      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${NIVEL_CHIP}`}>{g.n}</span>
    </div>
    <div className="mt-4 font-display text-base font-bold tracking-tight text-slate-900 leading-snug">{g.t}</div>
    <div className="mt-3 flex items-center justify-between">
      <span className="text-xs text-slate-500">{g.v} partidas</span>
      <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-900 group-hover:text-brand-orange transition-colors">
        <Play className="h-3.5 w-3.5 fill-current" /> Jugar
      </span>
    </div>
  </button>
);

/* ───────────── Lo que ve el estudiante ───────────── */
const VistaEstudiante = ({ onPlay }) => (
  <div data-testid="preview-game-zone-student">
    {/* cabecera */}
    <div className="rounded-3xl bg-slate-900 text-white p-8 relative overflow-hidden mb-6">
      <span className="absolute inset-0 opacity-[0.07]"
            style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "22px 22px" }} />
      <span className="absolute -right-10 -top-16 h-56 w-56 rounded-full bg-brand-orange/25 blur-2xl" />
      <div className="relative flex items-start gap-4">
        <span className="h-14 w-14 rounded-2xl grid place-items-center bg-white/15 ring-1 ring-white/25 shrink-0">
          <Gamepad2 className="h-7 w-7" />
        </span>
        <div>
          <Etiqueta className="text-white/50">Tu nivel: A2</Etiqueta>
          <h3 className="mt-1.5 font-display text-3xl font-bold tracking-tight">WSS Game Zone</h3>
          <p className="mt-1.5 text-sm text-white/70 max-w-lg">
            Jugá y practicá sin salir de la plataforma. Elegí un tema y arrancá por los de tu nivel.
          </p>
        </div>
      </div>
    </div>

    {/* destacados de la semana */}
    <div className="mb-10">
      <div className="flex items-center gap-2 mb-3">
        <Star className="h-4 w-4 text-amber-500 fill-amber-400" />
        <Etiqueta className="text-slate-500">Destacados de la semana</Etiqueta>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {DESTACADOS.map((g) => (
          <button key={g.t} onClick={onPlay}
                  className="group text-left rounded-2xl p-5 bg-gradient-to-br from-slate-900 to-slate-800 text-white transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white/15">{FUENTE[g.f].label}</span>
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-brand-orange">{g.n}</span>
            </div>
            <div className="mt-4 font-display text-lg font-bold tracking-tight leading-snug">{g.t}</div>
            <div className="mt-2 text-xs text-white/60">{g.tema}</div>
            <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold">
              <Play className="h-3.5 w-3.5 fill-current" /> Jugar ahora
            </span>
          </button>
        ))}
      </div>
    </div>

    {/* temas, y dentro de cada tema por nivel */}
    {TEMAS.map((tema) => (
      <div key={tema.key} className="mb-10">
        <div className="flex items-center gap-2.5 mb-4">
          <span className={`h-10 w-10 rounded-xl grid place-items-center ${tema.color}`}>
            <tema.icon className="h-5 w-5" />
          </span>
          <div>
            <div className="font-display text-xl font-bold tracking-tight text-slate-900">{tema.nombre}</div>
            <div className="text-xs text-slate-500">{tema.juegos.length} juegos</div>
          </div>
        </div>
        {["A1", "A2", "B1"].map((nivel) => {
          const juegos = tema.juegos.filter((g) => g.n === nivel);
          if (!juegos.length) return null;
          return (
            <div key={nivel} className="mb-5">
              <div className="flex items-center gap-2 mb-2.5">
                <GraduationCap className="h-3.5 w-3.5 text-slate-400" />
                <Etiqueta className="text-slate-400">
                  Nivel {nivel}{nivel === "A2" ? " · el tuyo" : ""}
                </Etiqueta>
                {nivel === "A2" && <span className="h-1.5 w-1.5 rounded-full bg-brand-orange" />}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {juegos.map((g) => <GameCard key={g.t} g={g} onPlay={onPlay} />)}
              </div>
            </div>
          );
        })}
      </div>
    ))}
  </div>
);

/* ───────────── Jugando dentro de la plataforma ───────────── */
const VistaJuego = ({ onClose }) => (
  <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="preview-game-player">
    <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-200">
      <div className="flex items-center gap-2.5 min-w-0">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">
          <span className="h-1.5 w-1.5 rounded-full bg-violet-500" /> Wordwall
        </span>
        <span className="font-display font-bold tracking-tight text-slate-900 truncate">Presente irregular: ruleta</span>
        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-900 text-white">A1</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button className="text-[13px] font-semibold text-slate-500 inline-flex items-center gap-1.5 hover:text-slate-900 transition-colors">
          <ExternalLink className="h-3.5 w-3.5" /> Abrir en una pestaña
        </button>
        <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-100 transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
    <div className="aspect-video bg-slate-50 grid place-items-center">
      <div className="text-center px-6">
        <Gamepad2 className="h-10 w-10 text-slate-300 mx-auto" />
        <p className="mt-3 text-sm text-slate-500 max-w-sm">
          Aquí va el juego, cargado dentro de la plataforma con el código que da
          Wordwall o Kahoot. El estudiante juega sin salir de aquí.
        </p>
      </div>
    </div>
    <div className="px-5 py-3 border-t border-slate-200 flex items-center gap-2 text-xs text-slate-500">
      <Trophy className="h-3.5 w-3.5 text-amber-500" /> Al terminar, el juego queda marcado como jugado en tus logros.
    </div>
  </div>
);

/* ───────────── Lo que ve el profe al agregar un juego ───────────── */
const VistaProfe = () => (
  <div className="max-w-xl bg-white border border-slate-200 rounded-2xl p-6" data-testid="preview-game-form">
    <div className="flex items-center gap-2.5 mb-5">
      <span className="h-10 w-10 rounded-xl grid place-items-center bg-slate-900 text-white">
        <Plus className="h-5 w-5" />
      </span>
      <div>
        <div className="font-display text-xl font-bold tracking-tight text-slate-900">Agregar un juego</div>
        <div className="text-xs text-slate-500">Lo puede hacer la administración y cualquier profesor</div>
      </div>
    </div>
    <div className="space-y-4">
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Enlace del juego</label>
        <div className="mt-1.5 h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 flex items-center text-sm text-slate-500">
          https://wordwall.net/resource/12345/espanol/presente-irregular
        </div>
        <p className="text-[11px] text-slate-500 mt-1.5">
          Pegá el enlace y la plataforma reconoce sola si es de Wordwall o de Kahoot, y saca el título.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Tema</label>
          <div className="mt-1.5 h-10 rounded-lg border border-slate-200 px-3 flex items-center text-sm text-slate-700">Verbos</div>
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Nivel</label>
          <div className="mt-1.5 h-10 rounded-lg border border-slate-200 px-3 flex items-center text-sm text-slate-700">A1</div>
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <span className="h-4 w-4 rounded border-2 border-slate-900 bg-slate-900 grid place-items-center">
          <Star className="h-2.5 w-2.5 text-white fill-white" />
        </span>
        Destacarlo esta semana
      </label>
      <div className="pt-2">
        <span className="inline-flex items-center rounded-full bg-slate-900 text-white text-sm font-semibold px-6 py-2.5">
          Guardar juego
        </span>
      </div>
    </div>
  </div>
);

export default function GameZonePreview() {
  const [jugando, setJugando] = useState(false);

  return (
    <div className="max-w-[1200px]" data-testid="game-zone-preview">
      <div className="mb-12">
        <Etiqueta className="text-brand-orange">Maqueta · datos de ejemplo</Etiqueta>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
          WSS Game Zone
        </h1>
        <p className="mt-2 text-sm text-slate-600 max-w-2xl">
          Pestaña propia para los juegos de Wordwall y Kahoot, por tema y dentro de cada
          tema por nivel. Nada está conectado todavía: decime qué cambio y lo armo.
        </p>
      </div>

      <Bloque testId="preview-bloque-estudiante" titulo="Lo que ve el estudiante"
              nota="Destacados arriba, luego cada tema con sus niveles. El suyo va marcado.">
        <VistaEstudiante onPlay={() => setJugando(true)} />
      </Bloque>

      <Bloque testId="preview-bloque-juego" titulo="Cuando le da «Jugar»"
              nota="El juego carga dentro de la plataforma; si se niega, está el botón para abrirlo aparte.">
        {jugando ? <VistaJuego onClose={() => setJugando(false)} /> : <VistaJuego onClose={() => {}} />}
      </Bloque>

      <Bloque testId="preview-bloque-profe" titulo="Lo que ve el profe para subir un juego"
              nota="Pega el enlace, elige tema y nivel, y listo.">
        <VistaProfe />
      </Bloque>
    </div>
  );
}
